import {
    Alert,
    Box,
    Button,
    Paper,
} from "@mui/material";
import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "../../../assets/css/time.css";
import {
    EntryWrapper,
    FormWrapper,
} from "../../../components/customStyles/form";
import Progress from "../../../components/progress/Progress";
import {
    defaultClosingSchedule,
    defaultOpeningSchedule,
} from "../../../models/Schedule";
import IAdminPageBuildingDetail from "../../../models/adminPage/adminPageBuildingDetail";
import IFloor from "../../../models/floor";
import IProfile from "../../../models/profile";
import ILocalEditRequest, { defaultLocalRequest } from "../../../models/requests/local/localEditRequest";
import { routes } from "../../../routes";
import buildingService from "../../../services/building/BuildingService";
import eslService from "../../../services/esl/EslService";
import CleaningScheduleService from "../../../services/local/CleaningScheduleService";
import localService from "../../../services/local/LocalService";
import profileService from "../../../services/profile/ProfileService";
import macAddressUtils from "../../../utils/macAddressUtils";
import LocalFormFields, { isServicePlusStickerCode } from "./LocalFormFields";
import EditOnly from "../../../components/permissions/EditOnly";

const SUCCESS_STATUS_CODE = 200;

const LocalAddPage = () => {
    const { t } = useTranslation();

    const navigate = useNavigate();

    const [localState, setLocalState] = useState<ILocalEditRequest>(defaultLocalRequest);
    const [buildings, setBuildings] = useState<IAdminPageBuildingDetail[]>([]);
    const [buildingFloors, setBuildingFloors] = useState<IFloor[]>([]);
    const [buildingProfiles, setBuildingProfiles] = useState<IProfile[]>([]);

    const [errorMessage, setErrorMessage] = useState("");

    const [isFormSending, setIsFormSending] = useState(false);

    const onCreateLocal = async (event: FormEvent) => {
        event.preventDefault();

        setErrorMessage("");
        setIsFormSending(true);

        if (localState.tagMacAddress) {
            const isEslMacValid = await eslService.GetIsValidMac(localState.tagMacAddress || "");

            if (!isEslMacValid) {
                setErrorMessage("table.local.macAddressErrorMessage");
                setIsFormSending(false);
                return;
            }
        }


        localService.PostLocal({ ...localState, openingSchedule: defaultOpeningSchedule, closingSchedule: defaultClosingSchedule })
            .then((response) => {

                if (response.status === SUCCESS_STATUS_CODE) {
                    CleaningScheduleService.PostCleaningSchedule({ ...localState.localCleaning, localId: response.data });

                    navigate(routes.tableRoute.locals);
                    return;
                }
                setErrorMessage("table.local.editErrorMessage");
            })
            .finally(() => setIsFormSending(false));
    };

    useEffect(() => {
        buildingService.GetBuildingsDetails().then((buildings) => {
            setBuildings(buildings);
        });
    }, []);

    useEffect(() => {
        if (localState.buildingId === 0) return;
        profileService.GetFloorsAndProfileByBuilding(localState.buildingId).then((profilesFloors) => {
            setBuildingProfiles(profilesFloors.profiles);
            setBuildingFloors(profilesFloors.floors);

        });

        setLocalState((prevState) => ({
            ...prevState,
            floorId: 0,
            profileId: 0,
        }));
    }, [localState.buildingId]);

    return (
        <Box component={Paper} sx={{ width: "100%" }}>
            <form autoComplete="off" onSubmit={onCreateLocal}>
                <FormWrapper>
                    {errorMessage && (
                        <Alert
                            severity="error"
                            sx={{ marginBottom: "1em" }}
                        >
                            {t(errorMessage)}
                        </Alert>
                    )}

                    <LocalFormFields
                        localState={localState}
                        setLocalState={setLocalState}
                        buildings={buildings}
                        buildingFloors={buildingFloors}
                        buildingProfiles={buildingProfiles}
                        localCleaning={localState.localCleaning}
                    />

                    <EntryWrapper sx={{ justifyContent: "flex-end" }}>
                        <Progress isLoading={isFormSending}>
                            <EditOnly>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                    disabled={
                                        !localState.name.trim() || !localState.buildingId || !localState.floorId ||
                                        !localState.profileId || localState.codes.length === 0 || !macAddressUtils.isMacAdrress(localState.tagMacAddress ?? "")
                                        || !isServicePlusStickerCode(localState.servicePlusStickerCode ?? "")
                                    }
                                    sx={{
                                        marginBlock: "1em",
                                    }}
                                >
                                    {t("table.local.create")}
                                </Button>
                            </EditOnly>
                        </Progress>
                    </EntryWrapper>
                </FormWrapper>
            </form>
        </Box>
    );
};

export default LocalAddPage;
